<template>
  <div class="emission-form-container">
    <div class="emission-form-layout">
      <div class="emission-form-main">
        <div class="form-header-actions">
          <el-button type="default" size="default" class="close-button" plain @click="handleClose">
            <el-icon><ArrowLeft /></el-icon>
            Volver
          </el-button>
        </div>

        <EmissionStepPersonal
          v-if="currentStep === 1"
          :form-data="formData"
          :can-proceed="canProceedStep1"
          :is-loading="isLoading"
          :allow-emission="props.allowEmission"
          @next="handleNextStep"
        />

        <EmissionStepAddress
          v-else-if="currentStep === 2"
          :form-data="formData"
          :can-proceed="canProceedStep2"
          :is-loading="isLoading"
          @back="handleBackStep"
          @next="handleNextStep"
        />

        <EmissionStepVehicle
          v-else-if="currentStep === 3"
          :form-data="formData"
          :can-proceed="canProceedStep3"
          :show-privacy-error="showPrivacyError"
          :emission-success="emissionSuccess"
          :allow-emission="props.allowEmission"
          :poliza-emitida="polizaEmitida"
          :documentos-disponibles="documentosDisponibles"
          :is-loading-documentos="isLoadingDocumentos"
          @back="handleBackStep"
          @next="handleVehiclePrimaryAction"
          @close="handleClose"
          @download-document="descargarDocumento"
        />

        <EmissionStepCompliance492
          v-else-if="props.allowEmission"
          :model-value="formData.articulo492Aceptado"
          :show-error="showComplianceError"
          :can-emit="canProceedStep4"
          :is-emitting="isEmitting"
          :is-downloading-offer="isDownloadingOffer"
          :is-sending-offer="isSendingOffer"
          @update:model-value="(value) => { formData.articulo492Aceptado = value; if (value) showComplianceError = false; }"
          @update:accepted-at="(value) => { formData.articulo492FechaAceptacion = value; }"
          @update:document-count="(value) => { formData.articulo492DocumentosCargados = value; }"
          @update:documents-metadata="(value) => { formData.articulo492DocumentosMetadata = value; }"
          @back="handleBackStep"
          @download-offer="handleDownloadOffer"
          @send-offer="handleSendOffer"
          @emit-policy="handleEmitPolicyClick"
        />
      </div>

      <div class="emission-summary">
        <EmissionSummary
          :quote-data="quoteData"
          :quotes-response="props.quotesResponse"
          :selected-plan-type="props.selectedPlanType"
          :selected-company="props.selectedCompany"
          :current-step="currentStep"
          :allow-emission="props.allowEmission"
          @back="handleClose"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { ArrowLeft } from '@element-plus/icons-vue';
import type { CotizaTodasResponse } from '@/types/quote.types';
import EmissionSummary from './EmissionSummary.vue';
import EmissionStepAddress from './emission/EmissionStepAddress.vue';
import EmissionStepCompliance492 from './emission/EmissionStepCompliance492.vue';
import EmissionStepPersonal from './emission/EmissionStepPersonal.vue';
import EmissionStepVehicle from './emission/EmissionStepVehicle.vue';
import { useEmissionForm } from '@/composables/useEmissionForm';
import { useEmissionSubmit } from '@/composables/useEmissionSubmit';

interface Props {
  quotesResponse: CotizaTodasResponse | null;
  selectedPlanType?: string;
  selectedPaymentMethod?: string;
  selectedCompany?: 'QUALITAS' | 'ANA';
  allowEmission?: boolean;
}

interface EmissionSuccessPayload {
  policyData: {
    policyNumber?: string;
    idPoliza?: number;
    idSolicitud?: number;
    planType?: string;
    primaTotal?: number;
  };
  documentos: Array<{
    Guid_Documento: string;
    Nombre?: string;
    Tipo?: string;
    Id_Solicitud?: number;
  }>;
}

const props = withDefaults(defineProps<Props>(), {
  selectedPlanType: 'Amplia',
  selectedPaymentMethod: 'Contado',
  selectedCompany: 'QUALITAS',
  allowEmission: true,
});

const emit = defineEmits<{
  close: [];
  success: [data: EmissionSuccessPayload];
}>();

const {
  formData,
  quoteData,
  currentStep,
  canProceedStep1,
  canProceedStep2,
  canProceedStep3,
  canProceedStep4,
  initFromQuoteStore,
  handleNextStep,
  handleBackStep,
} = useEmissionForm();

const {
  isEmitting,
  emissionSuccess,
  polizaEmitida,
  documentosDisponibles,
  isLoadingDocumentos,
  showPrivacyError,
  isDownloadingOffer,
  isSendingOffer,
  handleEmitPolicy,
  handleDownloadOffer,
  handleSendOffer,
  descargarDocumento,
} = useEmissionSubmit(formData, () => props, (data) => emit('success', data));

const isLoading = ref(false);
const showComplianceError = ref(false);

onMounted(initFromQuoteStore);

const handleClose = (): void => emit('close');

const handleVehiclePrimaryAction = (): void => {
  if (!canProceedStep3.value) {
    showPrivacyError.value = true;
    return;
  }

  showPrivacyError.value = false;

  if (props.allowEmission) {
    handleNextStep();
    return;
  }

  handleEmitPolicy();
};

const handleEmitPolicyClick = (): void => {
  if (!canProceedStep4.value) {
    showComplianceError.value = true;
    return;
  }

  showComplianceError.value = false;
  handleEmitPolicy();
};
</script>

<style src="@/styles/emission-form.css"></style>
